import { useMemo } from "react";
import type {
  BatchJob,
  CoverageGapRecheckItemResult,
  CoverageGapRecheckSummary,
} from "../../../../../lib/corpusApi";
import { narrowByOutcome, type BatchJobItemRow } from "../../panel/batch_job_item_rows";
import {
  useBatchJobItemList,
  type UseBatchJobItemListResult,
} from "../../panel/useBatchJobItemList";
import { useBatchJobTabs, type UseBatchJobTabsResult } from "../../panel/useBatchJobTabs";
import {
  COVERAGE_GAP_RECHECK_TABS,
  type CoverageGapRecheckTabConfig,
  type CoverageGapRecheckTabKey,
} from "./coverage_gap_recheck_tabs";

/** One list row: the raw item plus its check result. A failed item has none. */
export type CoverageGapRecheckRow = BatchJobItemRow<CoverageGapRecheckItemResult>;

/** What the panel header shows, read from the job's stored summary. */
export interface CoverageGapRecheckPanelSummary {
  answeredCount: number;
  unansweredCount: number;
}

export interface UseCoverageGapRecheckPanelResult {
  /** Null while the job has not finished, as its summary is only written at the end. */
  summary: CoverageGapRecheckPanelSummary | null;
  tabs: UseBatchJobTabsResult<CoverageGapRecheckTabKey, CoverageGapRecheckTabConfig>;
  list: UseBatchJobItemListResult<CoverageGapRecheckItemResult>;
}

const narrowCoverageGapRecheckResult = narrowByOutcome<CoverageGapRecheckItemResult>(
  new Set(["answered", "unanswered"])
);

/** Reads a job's stored summary into what the panel header shows; null when there is none yet. */
export function toPanelSummary(
  stored: Record<string, unknown> | null
): CoverageGapRecheckPanelSummary | null {
  if (!stored) return null;
  const summary = stored as unknown as CoverageGapRecheckSummary;
  return {
    answeredCount: summary.byOutcome.answered ?? 0,
    unansweredCount: summary.byOutcome.unanswered ?? 0,
  };
}

/** Drives the coverage_gap_recheck panel: the job's summary, and one paginated list per tab.
 *  Read-only — the check has no review; what to do with the gap is decided on the gap. */
export function useCoverageGapRecheckPanel(job: BatchJob): UseCoverageGapRecheckPanelResult {
  const tabs = useBatchJobTabs(COVERAGE_GAP_RECHECK_TABS, "unanswered");
  const list = useBatchJobItemList({
    job,
    filters: tabs.tab.apiFilters,
    resetKey: tabs.activeTab,
    narrow: narrowCoverageGapRecheckResult,
  });
  const summary = useMemo(() => toPanelSummary(job.summary), [job.summary]);

  return { summary, tabs, list };
}
